import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { GenerateDocumentModal } from '@/components/GenerateDocumentModal';
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, Edit, Trash2, File, Download, Upload, FileText, User, BookText, Shield, Key } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAI } from "@/hooks/useAI";
import { SignatureDashboard } from "@/components/signature/SignatureDashboard";
import { DigitalCertificateSignature } from "@/components/signature/DigitalCertificateSignature";

interface Document {
  id: string;
  title: string;
  description?: string;
  document_type: string;
  file_path?: string;
  file_size?: number;
  created_at: string;
  client_id?: string;
  case_id?: string;
  clients?: {
    name: string;
    email: string;
  };
  cases?: {
    title: string;
    case_number?: string;
  };
}

interface Client {
  id: string;
  name: string;
  email: string;
}

interface Case {
  id: string;
  title: string;
  case_number?: string;
}

const Documents = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const { toast } = useToast();
  const { summarizeDocument, loading: summarizing } = useAI();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    document_type: "",
    client_id: "",
    case_id: ""
  });

  const documentTypes = [
    "Petição",
    "Contrato",
    "Procuração",
    "Certidão",
    "Comprovante",
    "Parecer",
    "Relatório",
    "Sentença",
    "Recurso",
    "Outros"
  ];

  useEffect(() => {
    fetchDocuments();
    fetchClients();
    fetchCases();
  }, []);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('documents')
        .select(`
          *,
          clients:client_id (
            name,
            email
          ),
          cases:case_id (
            title,
            case_number
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Erro ao buscar documentos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os documentos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, email')
        .order('name');

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  const fetchCases = async () => {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('id, title, case_number')
        .order('title');

      if (error) throw error;
      setCases(data || []);
    } catch (error) {
      console.error('Erro ao buscar processos:', error);
    }
  };

  const handleFileUpload = async (file: File): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      return fileName;
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast({
        title: "Erro",
        description: "Não foi possível fazer upload do arquivo",
        variant: "destructive"
      });
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      let filePath = editingDocument?.file_path;
      let fileSize = editingDocument?.file_size;

      // Se há um novo arquivo para upload
      if (uploadFile) {
        filePath = await handleFileUpload(uploadFile);
        fileSize = uploadFile.size;
        if (!filePath) return; // Upload falhou
      }

      const dataToSave = {
        title: formData.title,
        description: formData.description || null,
        document_type: formData.document_type,
        file_path: filePath || null,
        file_size: fileSize || null,
        client_id: formData.client_id === "none" ? null : formData.client_id || null,
        case_id: formData.case_id === "none" ? null : formData.case_id || null,
        user_id: user.id
      };

      if (editingDocument) {
        const { error } = await supabase
          .from('documents')
          .update(dataToSave)
          .eq('id', editingDocument.id);

        if (error) throw error;
        toast({
          title: "Sucesso",
          description: "Documento atualizado com sucesso"
        });
      } else {
        const { error } = await supabase
          .from('documents')
          .insert([dataToSave]);

        if (error) throw error;
        toast({
          title: "Sucesso",
          description: "Documento criado com sucesso"
        });
      }

      setIsDialogOpen(false);
      setEditingDocument(null);
      setUploadFile(null);
      resetForm();
      fetchDocuments();
    } catch (error) {
      console.error('Erro ao salvar documento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o documento",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      document_type: "",
      client_id: "",
      case_id: ""
    });
  };

  const handleEdit = (document: Document) => {
    setEditingDocument(document);
    setFormData({
      title: document.title,
      description: document.description || "",
      document_type: document.document_type,
      client_id: document.client_id || "",
      case_id: document.case_id || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este documento?')) return;

    try {
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({
        title: "Sucesso",
        description: "Documento excluído com sucesso"
      });
      fetchDocuments();
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o documento",
        variant: "destructive"
      });
    }
  };

  const handleDownload = async (document: Document) => {
    if (!document.file_path) {
      toast({
        title: "Erro",
        description: "Este documento não possui arquivo anexado",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(document.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.title;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível baixar o documento",
        variant: "destructive"
      });
    }
  };

  const handleSummarize = async (document: Document) => {
    if (!document.file_path) {
      toast({
        title: "Erro",
        description: "Este documento não possui arquivo anexado para resumo.",
        variant: "destructive"
      });
      return;
    }

    // Check if the file is likely a text file based on extension
    const fileExt = document.file_path.split('.').pop()?.toLowerCase();
    const textFileExtensions = ['txt', 'md', 'json', 'csv', 'xml', 'html', 'js', 'ts', 'css', 'java', 'py', 'c', 'cpp', 'h', 'hpp', 'php', 'rb', 'go', 'rs', 'swift', 'kt', 'sql'];
    if (!fileExt || !textFileExtensions.includes(fileExt)) {
      toast({
        title: "Erro",
        description: "Apenas documentos de texto podem ser resumidos no momento.",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Processando",
      description: "Gerando resumo do documento...",
    });

    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(document.file_path);

      if (error) throw error;

      const reader = new FileReader();
      reader.onload = async (e) => {
        const content = e.target?.result as string;
        if (content) {
          const summary = await summarizeDocument(content);
          if (summary) {
            toast({
              title: "Resumo do Documento",
              description: summary,
              duration: 9000,
            });
          } else {
            toast({
              title: "Erro",
              description: "Não foi possível gerar o resumo.",
              variant: "destructive"
            });
          }
        } else {
          toast({
            title: "Erro",
            description: "Conteúdo do arquivo vazio ou ilegível.",
            variant: "destructive"
          });
        }
      };
      reader.onerror = (e) => {
        console.error("Erro ao ler arquivo:", e);
        toast({
          title: "Erro",
          description: "Não foi possível ler o conteúdo do arquivo.",
          variant: "destructive"
        });
      };
      reader.readAsText(data);

    } catch (error) {
      console.error('Erro ao resumir documento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível resumir o documento",
        variant: "destructive"
      });
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredDocuments = documents.filter(document => {
    const matchesSearch = document.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         document.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         document.clients?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         document.cases?.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || document.document_type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <Tabs defaultValue="documents" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="documents" className="flex items-center">
            <FileText className="w-4 h-4 mr-2" />
            Documentos
          </TabsTrigger>
          <TabsTrigger value="signatures" className="flex items-center">
            <Shield className="w-4 h-4 mr-2" />
            Assinaturas Eletrônicas
          </TabsTrigger>
          <TabsTrigger value="digital-certificate" className="flex items-center">
            <Key className="w-4 h-4 mr-2" />
            Certificado Digital
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Documentos</h1>
              <p className="text-muted-foreground">Gerencie seus documentos e arquivos</p>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => {
                  setEditingDocument(null);
                  setUploadFile(null);
                  resetForm();
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Documento
                </Button>
              </DialogTrigger>
          <GenerateDocumentModal />
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingDocument ? "Editar Documento" : "Novo Documento"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="document_type">Tipo de Documento *</Label>
                <Select value={formData.document_type} onValueChange={(value) => setFormData({ ...formData, document_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client_id">Cliente</Label>
                  <Select value={formData.client_id} onValueChange={(value) => setFormData({ ...formData, client_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum cliente</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="case_id">Processo Relacionado</Label>
                  <Select value={formData.case_id} onValueChange={(value) => setFormData({ ...formData, case_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um processo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum processo</SelectItem>
                      {cases.map((case_) => (
                        <SelectItem key={case_.id} value={case_.id}>
                          {case_.title} {case_.case_number ? `(${case_.case_number})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="file">Arquivo</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="cursor-pointer"
                />
                {editingDocument?.file_path && !uploadFile && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Arquivo atual: {editingDocument.title}
                  </p>
                )}
                {uploadFile && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Novo arquivo: {uploadFile.name} ({formatFileSize(uploadFile.size)})
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingDocument ? "Atualizar" : "Criar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar documentos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {documentTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDocuments.map((document) => (
          <Card key={document.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg line-clamp-1">{document.title}</CardTitle>
                    <Badge variant="outline">{document.document_type}</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  {document.file_path && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSummarize(document)}
                      disabled={summarizing}
                      title="Resumir Documento"
                    >
                      <BookText className="w-4 h-4" />
                    </Button>
                  )}
                  {document.file_path && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(document)}
                      title="Baixar Documento"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(document)}
                    title="Editar Documento"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(document.id)}
                    title="Excluir Documento"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {document.description && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {document.description}
                </p>
              )}
              
              {document.clients && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="w-4 h-4" />
                  {document.clients.name}
                </div>
              )}
              
              {document.cases && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-medium">Processo:</span>
                  {document.cases.title}
                </div>
              )}
              
              {document.file_path && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <File className="w-4 h-4" />
                  Arquivo: {formatFileSize(document.file_size)}
                </div>
              )}
              
              <p className="text-xs text-muted-foreground">
                Criado em: {new Date(document.created_at).toLocaleDateString('pt-BR')}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredDocuments.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium">Nenhum documento encontrado</h3>
          <p className="text-muted-foreground">
            {searchTerm || typeFilter !== "all" ? "Tente ajustar seus filtros" : "Comece criando seu primeiro documento"}
          </p>
        </div>
      )}
        </TabsContent>

        <TabsContent value="signatures" className="space-y-6">
          <SignatureDashboard />
        </TabsContent>

        <TabsContent value="digital-certificate" className="space-y-6">
          <DigitalCertificateSignature />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Documents;